import { v4 as uuidv4 } from 'uuid'

/**
 * Generic algorithm to transform flat document sections into hierarchical tree structure
 * 
 * @param {Array} flatSections - Array of section objects with at minimum a section identifier field
 * @param {Object} config - Configuration object
 * @param {string} config.sectionNameField - Field name containing the section identifier (e.g., 'section_name')
 * @param {string} config.displayNameField - Field name for display purposes (defaults to sectionNameField)
 * @param {RegExp} config.numberingPattern - Regex pattern to extract numbering (defaults to detecting numeric patterns)
 * @param {Array} config.nonNumberedPrefixes - Prefixes that indicate non-numbered items (e.g., ['Table', 'Figure'])
 * @param {string} config.nonNumberedStrategy - Strategy for non-numbered items: 'sibling' (same level) or 'child' (one level deeper)
 * @returns {Array} Array of sections with added id, name, header_level, order properties
 */
export function transformFlatSectionsToTree(flatSections, config = {}) {
    const {
        sectionNameField = 'section_name',
        displayNameField = sectionNameField,
        numberingPattern = /^(\d+(?:\.\d+)*(?:\.[A-Za-z]+(?:\.\d+)*)*)/,
        nonNumberedPrefixes = ['Table', 'Figure', 'Appendix'],
        nonNumberedStrategy = 'sibling' // 'sibling' or 'child'
    } = config

    if (!Array.isArray(flatSections) || flatSections.length === 0) {
        return []
    }

    const processedSections = []
    let currentParentStack = [] // Stack to track current parent hierarchy
    let previousSection = null // Track the immediately previous section

    flatSections.forEach((section, index) => {
        let sectionIdentifier = section[sectionNameField]
        if (!sectionIdentifier) {
            sectionIdentifier = section['title']
            if (!sectionIdentifier) {
                console.warn(`Section at index ${index} missing ${sectionNameField} and title`)
                return
            }
        }

        const processed = analyzeSection(sectionIdentifier, section, index, currentParentStack, {
            displayNameField,
            numberingPattern,
            nonNumberedPrefixes,
            nonNumberedStrategy,
            previousSection // Pass previous section for context
        })

        // Update parent stack based on this section's level
        updateParentStack(currentParentStack, processed)
        
        processedSections.push(processed)
        previousSection = processed // Update for next iteration
    })

    return processedSections
}

/**
 * Analyzes a single section to determine its hierarchy properties
 * @param {string} sectionIdentifier - The section name/identifier
 * @param {object} originalSection - The original section object
 * @param {number} index - Current section index in the array
 * @param {array} parentStack - Stack of current parent sections
 * @param {object} config - Configuration object including previousSection
 */
function analyzeSection(sectionIdentifier, originalSection, index, parentStack, config) {
    const { displayNameField, numberingPattern, nonNumberedPrefixes, nonNumberedStrategy, previousSection } = config
    
    // Check if this is a non-numbered item (table, figure, etc.)
    const isNonNumbered = nonNumberedPrefixes.some(prefix => 
        sectionIdentifier.toLowerCase().startsWith(prefix.toLowerCase())
    )

    let iterationsNeeded = 0, headerLevel, sectionNumber, cleanName

    if (isNonNumbered) {
        // For non-numbered items, we need better context detection
        let contextLevel = 6 // fallback
        
        // First, try to get level from the immediate preceding section
        if (previousSection) {
            contextLevel = previousSection.header_level
        } else {
            // If no previous section, try numbered parent from stack
            const lastNumberedParent = findLastNumberedParent(parentStack)
            if (lastNumberedParent) {
                contextLevel = lastNumberedParent.header_level
            }
        }
        
        // Apply strategy
        if (nonNumberedStrategy === 'child') {
            headerLevel = contextLevel + 1
        } else {
            // Sibling strategy: same level as context
            headerLevel = contextLevel
        }
        
        sectionNumber = null
        cleanName = sectionIdentifier
    } else {
        // Extract numbering pattern
        const numberMatch = sectionIdentifier.match(numberingPattern)
        if (numberMatch) {
            const rawSectionNumber = numberMatch[1]
            sectionNumber = normalizeSectionNumber(rawSectionNumber)  // Store the normalized section number
            headerLevel = calculateHeaderLevel(rawSectionNumber) // Calculate level from original (includes normalization)
            // Extract clean name (everything after the number)
            cleanName = sectionIdentifier.replace(numberingPattern, '').trim()
        } else {
            // For non-numbered items, we need better context detection
            let contextLevel = 6 // fallback
            
            // First, try to get level from the immediate preceding section
            if (previousSection) {
                contextLevel = previousSection.header_level
            } else {
                // If no previous section, try numbered parent from stack
                const lastNumberedParent = findLastNumberedParent(parentStack)
                if (lastNumberedParent) {
                    contextLevel = lastNumberedParent.header_level
                }
            }
            
            // Apply strategy
            if (nonNumberedStrategy === 'child') {
                headerLevel = contextLevel + 1
            } else {
                // Sibling strategy: same level as context
                headerLevel = contextLevel
            }
            
            sectionNumber = null
            cleanName = sectionIdentifier
        }
    }

    return {
        id: uuidv4(),
        name: originalSection[displayNameField] || cleanName,
        section_name: sectionIdentifier, // Keep original identifier
        header_level: headerLevel,
        order: index + 1, // Simple sequential ordering
        section_number: sectionNumber,
        is_non_numbered: isNonNumbered,
        ...originalSection // Spread all original properties
    }
}

/**
 * Normalizes section numbers by removing trailing zeros
 * Examples: "5.0" → "5", "3.2.0.0" → "3.2", "0.0.0" → "0"
 */
function normalizeSectionNumber(sectionNumber) {
    if (!sectionNumber) return sectionNumber
    
    const parts = sectionNumber.split('.')
    
    // Remove trailing parts that are "0"
    while (parts.length > 1 && parts[parts.length - 1] === '0') {
        parts.pop()
    }
    
    // If all parts were zeros, return "0"
    if (parts.length === 0 || (parts.length === 1 && parts[0] === '0')) {
        return '0'
    }
    
    return parts.join('.')
}

/**
 * Calculates header level based on section numbering depth
 */
function calculateHeaderLevel(sectionNumber) {
    if (!sectionNumber) return 1
    
    // Normalize the section number first
    const normalizedNumber = normalizeSectionNumber(sectionNumber)
    
    // Count the depth of numbering (e.g., "3.2.S.2.6.1.1" has depth of ~6)
    const parts = normalizedNumber.split('.')
    
    // Map depth to reasonable header levels (max 6 levels)
    return Math.min(parts.length, 6)
}

/**
 * Updates the parent stack to maintain hierarchy context
 */
function updateParentStack(parentStack, currentSection) {
    // Remove parents that are at same or deeper level
    while (parentStack.length > 0 && 
           parentStack[parentStack.length - 1].header_level >= currentSection.header_level) {
        parentStack.pop()
    }
    
    // Add current section to stack if it could have children
    if (!currentSection.is_non_numbered) {
        parentStack.push(currentSection)
    }
}

/**
 * Finds the last numbered section in the parent stack
 */
function findLastNumberedParent(parentStack) {
    for (let i = parentStack.length - 1; i >= 0; i--) {
        if (!parentStack[i].is_non_numbered) {
            return parentStack[i]
        }
    }
    return null
}

/**
 * Helper functions for working with the tree structure (compatible with existing code)
 */
export function getTopLevelSections(sections) {
    if (!sections || sections.length === 0) return []
    
    // Find the minimum header level in the sections
    const minHeaderLevel = Math.min(...sections.map(section => section.header_level))
    
    // Return sections that are at the minimum level (these are the top-level sections)
    return sections.filter(section => section.header_level === minHeaderLevel)
}

export function getChildSections(parentSection, allSections) {
    const parentOrder = parentSection.order
    const parentLevel = parentSection.header_level
    
    // Find the next section at the same level or higher in hierarchy (lower or equal header_level)
    const nextSameLevelSection = allSections.find(section => 
        section.header_level <= parentLevel && section.order > parentOrder
    )
    const maxOrder = nextSameLevelSection ? nextSameLevelSection.order : Infinity
    
    return allSections.filter(section => 
        section.header_level > parentLevel && 
        section.order > parentOrder && 
        section.order < maxOrder
    )
}

export function getAllDescendants(parentSection, allSections) {
    const descendants = []
    const parentOrder = parentSection.order
    const parentLevel = parentSection.header_level
    
    // Find the next section at the same level or higher in hierarchy (lower or equal header_level)
    const nextSameLevelSection = allSections.find(section => 
        section.header_level <= parentLevel && section.order > parentOrder
    )
    const maxOrder = nextSameLevelSection ? nextSameLevelSection.order : Infinity
    
    allSections.forEach(section => {
        if (section.order > parentOrder && 
            section.order < maxOrder && 
            section.header_level > parentLevel) {
            descendants.push(section)
        }
    })
    
    return descendants
}

/**
 * Alternative configuration presets for different document types
 */
export const DocumentTypeConfigs = {
    // For regulatory/pharmaceutical documents
    REGULATORY: {
        sectionNameField: 'section_name',
        numberingPattern: /^(\d+(?:\.\d+)*(?:\.[A-Za-z]+(?:\.\d+)*)*)/,
        nonNumberedPrefixes: ['Table', 'Figure', 'Appendix', 'Attachment'],
        nonNumberedStrategy: 'sibling' // Tables/figures at same level as sections
    },
    
    // For academic papers
    ACADEMIC: {
        sectionNameField: 'section_name', 
        numberingPattern: /^(\d+(?:\.\d+)*)/,
        nonNumberedPrefixes: ['Figure', 'Table', 'Appendix'],
        nonNumberedStrategy: 'sibling' // Figures/tables typically siblings
    },
    
    // For technical documentation
    TECHNICAL: {
        sectionNameField: 'section_name',
        numberingPattern: /^(\d+(?:\.\d+)*)/,
        nonNumberedPrefixes: ['Figure', 'Table', 'Code', 'Example'],
        nonNumberedStrategy: 'child' // Code examples often nested under sections
    },
    
    // Generic fallback
    GENERIC: {
        sectionNameField: 'section_name',
        numberingPattern: /^(\d+(?:\.\d+)*(?:\.[A-Za-z]+(?:\.\d+)*)*)/,
        nonNumberedPrefixes: ['Table', 'Figure'],
        nonNumberedStrategy: 'sibling' // Safe default
    }
} 